/**
 * Loads identity + scopes from ai_assistent (`GET /me/info`) for the current token.
 * Feature gates (text-to-data, chat agents, etc.) should use scopes here — not
 * `api_config.ai.features`.
 */
import { useEffect, useState } from 'react';
import { aiMeInfo } from '../api/customer/ai_assistent.api';
import type { AiMeInfo } from '../api/customer/ai_assistent.api';
import { getAiConfig } from './useAiConfig';
import { useAiReachability } from './useAiReachability';

export type AiAssistentInfoState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; info: AiMeInfo }
    | { status: 'error'; message: string };

/** True when scopes include `needle` or wildcard `*`. */
export function aiHasScope(scopes: string[] | undefined, needle: string): boolean {
    if (!scopes?.length) return false;
    if (scopes.includes('*')) return true;
    return scopes.includes(needle);
}

export const AI_SCOPE_PRODUCT_EXTRACTION = 'services.product_extraction';
export const AI_SCOPE_ROUTE_ASSIGN = 'services.route_assign';

/** Chat widget + `/chat` — generic completion scope also grants embed access. */
export const AI_SCOPE_CHAT_COMPLETE = 'ai.complete';
export const AI_SCOPE_SUPERVISOR = 'agents.supervisor';

/**
 * The supervisor CHAT feature specifically (the "עוזר אישי" chat tab).
 * Extraction-only tokens (`services.product_extraction` only) return false.
 * `ai.complete` is the completion engine, not chat — require agents.supervisor.
 */
export function aiHasAssistantAccess(scopes: string[] | undefined): boolean {
    if (!scopes?.length) return false;
    if (scopes.includes('*')) return true;
    return scopes.includes(AI_SCOPE_SUPERVISOR);
}

/**
 * May open the AI Assistant WIDGET at all (the ERP launcher icon → "app").
 *
 * The widget is the entry point to the user's WHOLE feature tree — orders
 * dashboard, document loader ("טעינה AI"), documents manager, supplier docs,
 * etc. — not just the supervisor chat. So it must be available to anyone with
 * ANY enabled AI feature, even if the chat/agent feature is turned off.
 * Tying the launcher to `aiHasAssistantAccess` (chat-only) wrongly hides the
 * whole app from extraction/orders users.
 */
export function aiHasAnyAppAccess(scopes: string[] | undefined): boolean {
    if (!scopes?.length) return false;
    if (scopes.includes('*')) return true;
    if (scopes.includes(AI_SCOPE_CHAT_COMPLETE)) return true;
    if (scopes.includes(AI_SCOPE_PRODUCT_EXTRACTION)) return true;
    if (scopes.includes('channels.manage')) return true;
    return scopes.some((s) => s.startsWith('agents.'));
}

export interface AiAssistantAvailability {
    hasAiConfig: boolean;
    serverReachable: boolean;
    serverChecking: boolean;
    scopesLoading: boolean;
    /** Token includes chat / agent scopes — i.e. the supervisor CHAT tab is on. */
    assistantEnabled: boolean;
    /** Mount drawer + FAB — true for ANY enabled AI feature (orders, docs, …). */
    available: boolean;
    /** Token has `api_config.ai` + `services.route_assign` (datasheet נהג זמני). */
    routeAssignEnabled: boolean;
}

/** Gate for MainLayout `AiAssistantDrawer` (chat bot), separate from text-to-data. */
/** Async gate: token from api_config.ai + `ai.complete` (or `*`) on GET /me/info. */
export async function hasAiCompleteAccess(): Promise<boolean> {
    const cfg = getAiConfig();
    if (!cfg?.token?.trim() || !cfg?.apiBase?.replace(/\/+$/, '')) return false;
    try {
        const info = await aiMeInfo();
        return aiHasScope(info.scopes, AI_SCOPE_CHAT_COMPLETE);
    } catch {
        return false;
    }
}

export interface AiAutomationAvailability {
    hasAiConfig: boolean;
    serverReachable: boolean;
    serverChecking: boolean;
    scopesLoading: boolean;
    /** Token includes `ai.complete` (debt automation / `/ai/complete`). */
    completeEnabled: boolean;
    /** Show automation toolbar buttons. */
    available: boolean;
}

/** Gate for all-debts automation (uses `/ai/complete`, not chat iframe). */
export function useAiAutomationAvailability(): AiAutomationAvailability {
    const cfg = getAiConfig();
    const reach = useAiReachability(cfg);
    const me = useAiAssistentInfo();

    const scopes = me.status === 'ready' ? me.info.scopes : undefined;
    const scopesLoading = Boolean(cfg?.token) && me.status === 'loading';
    const completeEnabled = aiHasScope(scopes, AI_SCOPE_CHAT_COMPLETE);
    const hasAiConfig = Boolean(cfg?.token);
    const serverReachable = reach === 'ok';
    const serverChecking = reach === 'checking';

    const available =
        hasAiConfig
        && serverReachable
        && completeEnabled
        && !scopesLoading
        && me.status !== 'error';

    return {
        hasAiConfig,
        serverReachable,
        serverChecking,
        scopesLoading,
        completeEnabled: hasAiConfig ? completeEnabled : false,
        available,
    };
}

export function useAiAssistantAvailability(): AiAssistantAvailability {
    const cfg = getAiConfig();
    const reach = useAiReachability(cfg);
    const me = useAiAssistentInfo();

    const scopes = me.status === 'ready' ? me.info.scopes : undefined;
    const scopesLoading = Boolean(cfg?.token) && me.status === 'loading';
    const assistantEnabled = aiHasAssistantAccess(scopes);
    const anyAppAccess = aiHasAnyAppAccess(scopes);
    const hasAiConfig = Boolean(cfg?.token);
    const serverReachable = reach === 'ok';
    const serverChecking = reach === 'checking';
    const routeAssignEnabled = hasAiConfig && aiHasScope(scopes, AI_SCOPE_ROUTE_ASSIGN)
        && serverReachable && !scopesLoading && me.status !== 'error';

    // Mount the launcher for ANY enabled AI feature — the widget is the entry
    // to the whole feature tree, not just the supervisor chat.
    const available =
        hasAiConfig
        && serverReachable
        && anyAppAccess
        && !scopesLoading
        && me.status !== 'error';

    return {
        hasAiConfig,
        serverReachable,
        serverChecking,
        scopesLoading,
        assistantEnabled: hasAiConfig ? assistantEnabled : false,
        available,
        routeAssignEnabled,
    };
}

/** True when the current api_config.ai token includes `services.route_assign`. */
export function useHasRouteAssignScope(): boolean {
    const me = useAiAssistentInfo();
    return me.status === 'ready' && aiHasScope(me.info.scopes, AI_SCOPE_ROUTE_ASSIGN);
}

export function useAiAssistentInfo(): AiAssistentInfoState {
    const cfg = getAiConfig();
    const token = cfg?.token?.trim() ?? '';
    const apiBase = cfg?.apiBase?.replace(/\/+$/, '') ?? '';

    const [state, setState] = useState<AiAssistentInfoState>({ status: 'idle' });

    useEffect(() => {
        if (!token || !apiBase) {
            setState({ status: 'idle' });
            return;
        }

        let cancelled = false;
        setState({ status: 'loading' });

        aiMeInfo()
            .then((info) => {
                if (!cancelled) setState({ status: 'ready', info });
            })
            .catch((err) => {
                if (!cancelled) {
                    setState({
                        status: 'error',
                        message: err instanceof Error ? err.message : String(err),
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [token, apiBase]);

    return state;
}
